'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Bars3Icon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { SITE_NAME } from '@/lib/site';
import { MARKETING_ROUTES } from '@/lib/marketingRoutes';

// Anchor ditulis absolut ('/#fitur', bukan '#fitur') supaya tetap berfungsi
// saat nav dipakai di halaman marketing selain landing.
const links = [
  { href: '/#fitur', label: 'Fitur' },
  { href: '/#pelanggan', label: 'Untuk Pelanggan' },
  { href: '/#biaya', label: 'Biaya' },
  { href: '/blog', label: 'Blog' },
];

/**
 * `solid` dipakai halaman yang bagian atasnya BUKAN hero gelap — mis. artikel
 * blog. Tanpa ini, tulisan nav yang berwarna putih jadi tak terlihat di atas
 * latar putih.
 */
export default function LandingNav({ solid = false }: { solid?: boolean }) {
  const [discroll, setDiscroll] = useState(false);
  const scrolled = solid || discroll;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setDiscroll(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || open
          ? 'bg-white/90 shadow-sm backdrop-blur-md'
          : 'bg-transparent'
      }`}
    >
      <nav className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-3 sm:px-6">
        <Link href="/" className="flex items-center gap-2.5" aria-label={SITE_NAME}>
          <Image
            src="/logo.png"
            alt=""
            width={259}
            height={200}
            priority
            className="h-9 w-auto object-contain"
          />
          <span
            className={`text-lg font-bold tracking-tight ${
              scrolled || open ? 'text-[#293aa1]' : 'text-white'
            }`}
          >
            {SITE_NAME}
          </span>
        </Link>

        <div className="hidden items-center gap-7 lg:flex">
          {/* Menu Solusi: tautan internal ke seluruh halaman keyword. */}
          <div className="group relative">
            <button
              type="button"
              className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                scrolled ? 'text-gray-600 hover:text-[#293aa1]' : 'text-blue-50 hover:text-white'
              }`}
            >
              Solusi
              <ChevronDownIcon className="h-4 w-4 transition-transform group-hover:rotate-180" />
            </button>
            <div className="invisible absolute left-1/2 top-full w-80 -translate-x-1/2 pt-3 opacity-0 transition-all group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white p-2 shadow-xl">
                {MARKETING_ROUTES.map((r) => (
                  <Link
                    key={r.path}
                    href={r.path}
                    className="block rounded-lg px-3 py-2.5 hover:bg-blue-50"
                  >
                    <span className="block text-sm font-semibold text-gray-900">{r.judul}</span>
                    <span className="block text-xs text-gray-500">{r.ringkas}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {links.map((l) => (
            <Link
              key={l.href}
              href={l.href}
              className={`text-sm font-medium transition-colors ${
                scrolled ? 'text-gray-600 hover:text-[#293aa1]' : 'text-blue-50 hover:text-white'
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="hidden items-center gap-2 lg:flex">
          <Link
            href="/login"
            className={`rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${
              scrolled
                ? 'text-[#293aa1] hover:bg-blue-50'
                : 'text-white hover:bg-white/10'
            }`}
          >
            Masuk
          </Link>
          <Link
            href="/login"
            className="rounded-lg bg-amber-400 px-4 py-2.5 text-sm font-bold text-[#1b2461] shadow-sm transition-all hover:bg-amber-300 hover:shadow-md"
          >
            Coba Gratis
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? 'Tutup menu' : 'Buka menu'}
          className={`flex h-11 w-11 items-center justify-center rounded-lg transition-colors lg:hidden ${
            scrolled || open ? 'text-[#293aa1] hover:bg-blue-50' : 'text-white hover:bg-white/10'
          }`}
        >
          {open ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
        </button>
      </nav>

      {open && (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-gray-100 bg-white px-5 pb-5 pt-3 lg:hidden">
          <div className="flex flex-col">
            {links.map((l) => (
              <Link
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-3 text-base font-medium text-gray-700 hover:bg-blue-50 hover:text-[#293aa1]"
              >
                {l.label}
              </Link>
            ))}
          </div>

          <p className="mt-3 px-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
            Solusi
          </p>
          <div className="flex flex-col">
            {MARKETING_ROUTES.map((r) => (
              <Link
                key={r.path}
                href={r.path}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-[#293aa1]"
              >
                {r.judul}
              </Link>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Link
              href="/login"
              className="rounded-lg border border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-700"
            >
              Masuk
            </Link>
            <Link
              href="/login"
              className="rounded-lg bg-amber-400 px-4 py-3 text-center text-sm font-bold text-[#1b2461]"
            >
              Coba Gratis
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
